import { theme, Typography } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { UsageHistogram } from '@/types/stats';

const CHART_HEIGHT_PX = 280;
const ANIMATION_DURATION_MS = 600;

const ENDPOINT_COLORS: Record<string, string> = {
  compute_route: '#1677ff',
  compute_matrix: '#13c2c2',
  route_geometry: '#722ed1',
};

interface ChartRow {
  day: string;
  [endpoint: string]: string | number;
}

interface UsageHistogramChartProps {
  readonly histogram: UsageHistogram;
}

export default function UsageHistogramChart({ histogram }: UsageHistogramChartProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { rows, endpoints } = useMemo(() => buildChartData(histogram), [histogram]);

  if (rows.length === 0) {
    return (
      <Typography.Text type="secondary">
        {t('stats.histogram.empty')}
      </Typography.Text>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT_PX}>
      <BarChart data={rows as ChartRow[]}>
        <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
        <XAxis dataKey="day" stroke={token.colorTextSecondary} />
        <YAxis stroke={token.colorTextSecondary} allowDecimals={false} />
        <Tooltip
          contentStyle={{
            background: token.colorBgElevated,
            border: `1px solid ${token.colorBorder}`,
            borderRadius: token.borderRadius,
          }}
        />
        <Legend />
        {endpoints.map((endpoint) => (
          <Bar
            key={endpoint}
            dataKey={endpoint}
            stackId="calls"
            fill={ENDPOINT_COLORS[endpoint] ?? token.colorPrimary}
            animationDuration={ANIMATION_DURATION_MS}
            animationEasing="ease-out"
            radius={[2, 2, 0, 0]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

interface BuiltChartData {
  readonly rows: readonly ChartRow[];
  readonly endpoints: readonly string[];
}

function buildChartData(histogram: UsageHistogram): BuiltChartData {
  const perDay = new Map<string, Record<string, number>>();
  const endpointsSet = new Set<string>();

  for (const bucket of histogram.buckets) {
    endpointsSet.add(bucket.endpoint);
    const day = perDay.get(bucket.day) ?? {};
    day[bucket.endpoint] = (day[bucket.endpoint] ?? 0) + bucket.count;
    perDay.set(bucket.day, day);
  }

  const endpoints = Array.from(endpointsSet).sort();
  const rows: ChartRow[] = Array.from(perDay.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, counts]) => {
      const row: ChartRow = { day };
      for (const endpoint of endpoints) {
        row[endpoint] = counts[endpoint] ?? 0;
      }
      return row;
    });

  return { rows, endpoints };
}
